import { useState, useEffect, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AdminPageHeader from "@/components/admin/AdminPageHeader";
import { toast } from "sonner";
import { Bell, Mail, History, Send, Loader2, Inbox, Shield, XCircle } from "lucide-react";

const NOTIFY_TARGETS = [
  { value: "all_coaches", label: "Tous les coachs" },
  { value: "all_students", label: "Tous les élèves" },
  { value: "all", label: "Toute la plateforme" },
];

const EMAIL_TARGETS = [
  { value: "all_coaches", label: "Tous les coachs" },
  { value: "all_students", label: "Tous les élèves" },
  { value: "all", label: "Tous les utilisateurs" },
];

const STATUS_LABELS = {
  queued: "En file",
  processing: "Envoi en cours",
  completed: "Terminé",
  cancelled: "Annulé",
};

function makeRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID().replace(/-/g, "");
  }
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function CampaignProgress({ campaign, onRefresh, onCancel, cancelling }) {
  if (!campaign) return null;

  const pct = campaign.progress_pct ?? 0;
  const done = campaign.status === "completed" || campaign.status === "cancelled";

  return (
    <Card className="border-blue-200 bg-blue-50/40 mt-4">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-slate-900 truncate">{campaign.subject}</p>
          <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${
            campaign.status === "completed" ? "bg-green-100 text-green-700" :
            campaign.status === "cancelled" ? "bg-red-100 text-red-700" :
            "bg-blue-100 text-blue-700"
          }`}>
            {STATUS_LABELS[campaign.status] ?? campaign.status}
          </span>
        </div>
        <div className="h-2 bg-white rounded-full overflow-hidden border border-blue-100">
          <div
            className={`h-full transition-all duration-500 ${
              campaign.status === "cancelled" ? "bg-red-400" : done ? "bg-green-500" : "bg-blue-500"
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600">
          <span>{campaign.sent_count ?? 0} envoyés</span>
          <span>{campaign.failed_count ?? 0} échecs</span>
          {(campaign.cancelled_count ?? 0) > 0 && (
            <span>{campaign.cancelled_count} annulés</span>
          )}
          <span>{campaign.pending_count ?? campaign.pending ?? 0} en attente</span>
          <span>{campaign.total_count ?? campaign.total ?? 0} total</span>
        </div>
        {!done && (
          <p className="text-xs text-blue-600 flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />
            Envoi sécurisé en arrière-plan — vous pouvez fermer cette page
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {!done && onCancel && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
              disabled={cancelling}
              onClick={onCancel}
            >
              {cancelling ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
              Annuler la campagne
            </Button>
          )}
          {done && onRefresh && (
            <Button type="button" variant="outline" size="sm" className="rounded-full h-8 text-xs" onClick={onRefresh}>
              Voir les campagnes
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminCommunications() {
  const [tab, setTab] = useState("notify");
  const [history, setHistory] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);

  const [notifyTarget, setNotifyTarget] = useState("all_coaches");
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [sendingNotify, setSendingNotify] = useState(false);

  const [emailTarget, setEmailTarget] = useState("all");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const pollActiveRef = useRef(false);
  const pollTickRef = useRef(0);

  const loadHistory = () => {
    setLoadingHistory(true);
    api.get("/admin/notifications-history")
      .then((r) => setHistory(r.data))
      .catch(() => toast.error("Erreur chargement historique"))
      .finally(() => setLoadingHistory(false));
  };

  const loadCampaigns = useCallback(() => {
    setLoadingCampaigns(true);
    api.get("/admin/email-campaigns")
      .then((r) => setCampaigns(r.data))
      .catch(() => {})
      .finally(() => setLoadingCampaigns(false));
  }, []);

  const stopPolling = useCallback(() => {
    pollActiveRef.current = false;
    pollTickRef.current = 0;
    setIsPolling(false);
  }, []);

  const pollCampaign = useCallback((campaignId) => {
    stopPolling();
    pollActiveRef.current = true;
    setIsPolling(true);

    const runPoll = async () => {
      if (!pollActiveRef.current) return;
      try {
        if (pollTickRef.current % 3 === 0) {
          await api.post(`/admin/email-campaigns/${campaignId}/process`).catch(() => {});
        }

        const { data } = await api.get(`/admin/email-campaigns/${campaignId}`);
        setActiveCampaign(data);

        if (data.status === "completed" || data.status === "cancelled") {
          stopPolling();
          toast.success(data.message || "Campagne terminée");
          loadCampaigns();
          return;
        }

        pollTickRef.current += 1;
        if (pollActiveRef.current) {
          setTimeout(runPoll, 2500);
        }
      } catch {
        stopPolling();
        toast.error("Erreur de suivi — l'envoi continue en arrière-plan via le serveur");
      }
    };

    api.post(`/admin/email-campaigns/${campaignId}/process`)
      .then(() => runPoll())
      .catch(() => runPoll());
  }, [stopPolling, loadCampaigns]);

  useEffect(() => {
    if (tab === "history") loadHistory();
    if (tab === "campaigns") loadCampaigns();
  }, [tab, loadCampaigns]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const sendNotify = async (e) => {
    e.preventDefault();
    if (!notifyTitle.trim() || !notifyMessage.trim()) return;
    setSendingNotify(true);
    try {
      const { data } = await api.post("/admin/notify", {
        target: notifyTarget,
        title: notifyTitle.trim(),
        message: notifyMessage.trim(),
      });
      toast.success(`${data.sent} notification(s) envoyée(s)`);
      setNotifyTitle("");
      setNotifyMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSendingNotify(false);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    if (!emailSubject.trim() || !emailBody.trim()) return;
    setSendingEmail(true);
    setActiveCampaign(null);
    const requestId = makeRequestId();
    try {
      const { data } = await api.post("/admin/send-email", {
        target: emailTarget,
        subject: emailSubject.trim(),
        body: emailBody.trim(),
        request_id: requestId,
      });
      setActiveCampaign(data);
      toast.success(`${data.queued} email(s) en file — envoi sécurisé démarré`);
      if (data.campaign_id && !["completed", "cancelled"].includes(data.status)) {
        pollCampaign(data.campaign_id);
      } else if (data.status === "completed") {
        toast.success(data.message);
        loadCampaigns();
      }
    } catch (err) {
      const status = err.response?.status;
      const msg = err.response?.data?.detail || err.response?.data?.error || "Erreur";
      toast.error(status === 429 ? `Limite atteinte : ${msg}` : msg);
    } finally {
      setSendingEmail(false);
    }
  };

  const cancelActiveCampaign = async () => {
    if (!activeCampaign?.campaign_id) return;
    if (!window.confirm("Annuler les emails encore en attente ?")) return;
    setCancelling(true);
    try {
      const { data } = await api.post(`/admin/email-campaigns/${activeCampaign.campaign_id}/cancel`);
      setActiveCampaign(data);
      stopPolling();
      toast.success(data.message || "Campagne annulée");
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'annuler");
    } finally {
      setCancelling(false);
    }
  };

  const resumeCampaign = async (campaignId) => {
    setActiveCampaign(campaigns.find((c) => c.campaign_id === campaignId) || null);
    setTab("email");
    pollCampaign(campaignId);
    toast.info("Reprise de l'envoi...");
  };

  const tabs = [
    { id: "notify", label: "Notification in-app", icon: Bell },
    { id: "email", label: "Email de masse", icon: Mail },
    { id: "campaigns", label: "Campagnes email", icon: Inbox },
    { id: "history", label: "Historique notifs", icon: History },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Communications"
        subtitle="Annonces in-app, emails de masse sécurisés avec file d'attente"
      />

      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-medium border transition-colors ${
              tab === t.id ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {tab === "notify" && (
        <Card className="border-slate-200 max-w-2xl">
          <CardContent className="p-6">
            <form onSubmit={sendNotify} className="space-y-4">
              <div>
                <Label>Destinataires</Label>
                <Select value={notifyTarget} onValueChange={setNotifyTarget}>
                  <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {NOTIFY_TARGETS.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Titre</Label>
                <Input value={notifyTitle} onChange={(e) => setNotifyTitle(e.target.value)} className="mt-1.5 h-11" placeholder="Annonce importante" />
              </div>
              <div>
                <Label>Message</Label>
                <textarea
                  value={notifyMessage}
                  onChange={(e) => setNotifyMessage(e.target.value)}
                  rows={4}
                  className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                  placeholder="Contenu de la notification..."
                />
              </div>
              <Button type="submit" disabled={sendingNotify} className="rounded-full gap-2">
                {sendingNotify ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Envoyer la notification
              </Button>
            </form>
          </CardContent>
        </Card>
      )}

      {tab === "email" && (
        <div className="max-w-2xl">
          <Card className="border-slate-200">
            <CardContent className="p-6">
              <div className="text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 mb-4 space-y-1.5">
                <p className="flex items-center gap-1.5 font-medium">
                  <Shield className="w-3.5 h-3.5 shrink-0" />
                  File d&apos;attente sécurisée
                </p>
                <ul className="list-disc list-inside text-blue-600/90 space-y-0.5 pl-1">
                  <li>1 seul email par destinataire (anti-doublon garanti)</li>
                  <li>Envoi par lots de 10, throttling SMTP, retry automatique</li>
                  <li>Utilisateurs bannis exclus · HTML filtré · quota 5 campagnes/h</li>
                  <li>L&apos;envoi continue en arrière-plan même si vous fermez la page</li>
                </ul>
              </div>
              <form onSubmit={sendEmail} className="space-y-4">
                <div>
                  <Label>Destinataires</Label>
                  <Select value={emailTarget} onValueChange={setEmailTarget}>
                    <SelectTrigger className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EMAIL_TARGETS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sujet</Label>
                  <Input
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    className="mt-1.5 h-11"
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label>Corps du message (texte ou HTML simple)</Label>
                  <textarea
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    rows={8}
                    maxLength={50000}
                    className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-y"
                    placeholder="Bonjour, ..."
                  />
                </div>
                <Button type="submit" disabled={sendingEmail || isPolling} className="rounded-full gap-2">
                  {sendingEmail || isPolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {isPolling ? "Envoi en cours..." : "Lancer la campagne"}
                </Button>
              </form>
            </CardContent>
          </Card>
          <CampaignProgress
            campaign={activeCampaign}
            onRefresh={() => setTab("campaigns")}
            onCancel={cancelActiveCampaign}
            cancelling={cancelling}
          />
        </div>
      )}

      {tab === "campaigns" && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loadingCampaigns ? (
              <p className="text-center py-12 text-slate-400">Chargement...</p>
            ) : campaigns.length === 0 ? (
              <p className="text-center py-12 text-slate-400 text-sm">Aucune campagne email</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {campaigns.map((c) => (
                  <div key={c.campaign_id} className="px-6 py-4 flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{c.subject}</p>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {c.sent_count}/{c.total_count} envoyés
                        {c.failed_count > 0 && ` · ${c.failed_count} échecs`}
                        {(c.cancelled_count ?? 0) > 0 && ` · ${c.cancelled_count} annulés`}
                        {" · "}{EMAIL_TARGETS.find((t) => t.value === c.target)?.label ?? c.target}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                      c.status === "completed" ? "bg-green-50 text-green-700" :
                      c.status === "cancelled" ? "bg-red-50 text-red-700" :
                      c.status === "processing" ? "bg-blue-50 text-blue-700" :
                      "bg-slate-100 text-slate-600"
                    }`}>
                      {STATUS_LABELS[c.status] ?? c.status}
                    </span>
                    <span className="text-xs text-slate-400 hidden sm:block">
                      {c.created_at ? new Date(c.created_at).toLocaleString("fr-FR") : ""}
                    </span>
                    {c.status !== "completed" && c.status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="rounded-full h-8 text-xs" onClick={() => resumeCampaign(c.campaign_id)}>
                        Reprendre
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === "history" && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loadingHistory ? (
              <p className="text-center py-12 text-slate-400">Chargement...</p>
            ) : history.length === 0 ? (
              <p className="text-center py-12 text-slate-400 text-sm">Aucune annonce envoyée</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {history.map((h) => (
                  <div key={h.notification_id} className="px-6 py-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{h.title}</p>
                        <p className="text-sm text-slate-600 mt-1">{h.message}</p>
                        <p className="text-xs text-slate-400 mt-2">→ {h.recipient_name}</p>
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">
                        {h.created_at ? new Date(h.created_at).toLocaleString("fr-FR") : ""}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}